import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  InputAdornment,
  Card,
  Typography,
  Stack,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Edit,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { Switch } from "@mui/material";
import api from "../services/api";
import { toast } from "react-toastify";
import Header from "./Header";

const Products = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState("");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getData = async () => {
      try {
        const res = await api.get("/getallproduct/Admin");
        const { data } = res.data;
        setData(data);
        setFilteredData(data);
      } catch (error) {
        toast.error("Failed to fetch products");
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, []);

  useEffect(() => {
    if (searchText) {
      const filtered = data.filter((item) =>
        item.ProductName.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
  }, [searchText, data]);

  const handleDelete = (id) => {
    setOpen(true);
    setDeleteItem(id);
  };

  const confirmDelete = async () => {
    try {
      const res = await api.delete(`/deleteProduct/${deleteItem}`);
      toast.success(res.data.message);
      setData(data.filter((item) => item._id !== deleteItem));
      setOpen(false);
    } catch (error) {
      toast.error("Error while deleting product");
    }
  };

  const handleEdit = (item) => {
    navigate(`/admin/AddProduct/${item._id}`);
  };

  const handleToggleOffer = async (productId, currentValue) => {
    try {
      await api.post("/UpdateProduct", { _id: productId, isOffer: !currentValue });
      setData(data.map(item => item._id === productId ? { ...item, isOffer: !currentValue } : item));
      toast.success(`Product ${!currentValue ? 'added to' : 'removed from'} offers`);
    } catch (error) {
      toast.error("Failed to update offer status");
    }
  };

  const rows = filteredData.map((item, index) => ({
    id: item._id,
    serialNo: index + 1,
    name: item.ProductName,
    mrp: `Rs.${item.ProductMRP}`,
    price: `Rs.${item.ProductPrice}`,
    imgUrl: item.ProductMainImgUrl,
    shortDesc: item.ProductShortDesc,
    category: item.ProductCategory,
    subCategory: item.ProductSubCategory,
    size: item.ProductSize,
    quantity: item.ProductQuantity,
    status: item.Status,
    isOffer: item.isOffer || false,
    actions: item,
  }));

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "imgUrl",
      headerName: "Image",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
          <img
            src={params.row.imgUrl}
            alt={params.row.name}
            style={{
              width: 60,
              height: 60,
              objectFit: "cover",
              borderRadius: 8,
              border: "2px solid #e0e0e0",
            }}
          />
        </Box>
      ),
    },
    {
      field: "name",
      headerName: "Product Name",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "category",
      headerName: "Category",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={params.value?.CategoryName || "N/A"}
          size="small"
          sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }}
        />
      ),
    },
    {
      field: "mrp",
      headerName: "MRP",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>    
        <Typography
          variant="body2"
          sx={{ textDecoration: "line-through", color: "#999" }}
        >
          {params.value}
        </Typography>
        </Box>
      ),
    },
    {
      field: "price",
      headerName: "Price",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>    
        <Typography variant="body2" sx={{ fontWeight: "bold", color: "#2e7d32" }}>
          {params.value}
        </Typography>
        </Box>
      ),
    },
    {
      field: "quantity",
      headerName: "Stock",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value > 10 ? "success" : params.value > 0 ? "warning" : "error"}
        />
      ),
    },
    {
      field: "isOffer",
      headerName: "Offer",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Switch
          checked={params.value}
          onChange={() => handleToggleOffer(params.row.id, params.value)}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: '#ff6b35' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ff6b35' },
          }}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <Chip
            label={params.value === "Accepted" ? "Active" : "Inactive"}
            size="small"
            sx={{ bgcolor: params.value === "Accepted" ? "#e8f5e9" : "#ffebee", color: params.value === "Accepted" ? "#2e7d32" : "#c62828", fontWeight: 600 }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Stack direction="row" spacing={1} justifyContent="center" mt={2}>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.value)}
            sx={{
              bgcolor: "#e3f2fd",
              "&:hover": { bgcolor: "#bbdefb" },
            }}
          >
            <Edit fontSize="small" sx={{ color: "#1976d2" }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id)}
            sx={{
              bgcolor: "#ffebee",
              "&:hover": { bgcolor: "#ffcdd2" },
            }}
          >
            <DeleteIcon fontSize="small" sx={{ color: "#d32f2f" }} />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f7fa" }}>
      <Header />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: { md: "28px" },
          mt: "64px",
          p: 3,
        }}
      >
        <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" color="#1a237e">
                Products Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your product inventory
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/admin/AddProduct")}
              sx={{
                bgcolor: "#1976d2",
                "&:hover": { bgcolor: "#1565c0" },
                textTransform: "none",
                px: 3,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
              }}
            >
              Add Product
            </Button>
          </Box>

          {/* Search Bar */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search products by name..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#757575" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#fafafa",
                },
              }}
            />
          </Box>

          {/* Stats Cards */}
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Card sx={{ flex: 1, p: 2, bgcolor: "#e3f2fd", border: "1px solid #90caf9" }}>
              <Typography variant="body2" color="#1565c0">Total Products</Typography>
              <Typography variant="h4" fontWeight="bold" color="#0d47a1">{data.length}</Typography>
            </Card>
            <Card sx={{ flex: 1, p: 2, bgcolor: "#e8f5e9", border: "1px solid #81c784" }}>
              <Typography variant="body2" color="#2e7d32">In Stock</Typography>
              <Typography variant="h4" fontWeight="bold" color="#1b5e20">
                {data.filter(p => p.ProductQuantity > 0).length}
              </Typography>
            </Card>
            <Card sx={{ flex: 1, p: 2, bgcolor: "#fff3e0", border: "1px solid #ffb74d" }}>
              <Typography variant="body2" color="#e65100">Low Stock</Typography>
              <Typography variant="h4" fontWeight="bold" color="#e65100">
                {data.filter(p => p.ProductQuantity > 0 && p.ProductQuantity <= 10).length}
              </Typography>
            </Card>
          </Stack>

          {/* DataGrid */}
          <Box sx={{ height: 600, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              loading={loading}
              disableSelectionOnClick
              rowHeight={80}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
              }}
              sx={{
                border: "none",
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #f0f0f0",
                },
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: "#1565c0",
                  borderRadius: "8px 8px 0 0",
                  overflow: "hidden",
                  color: "#000",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 600,
                  color: "#000",
                },
              }}
            />
          </Box>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ bgcolor: "#ffebee", color: "#c62828" }}>
            Confirm Delete
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography>Are you sure you want to delete this product? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpen(false)} sx={{ color: "#757575" }}>
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              variant="contained"
              color="error"
              sx={{ textTransform: "none" }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Products;
